'use client'

import {
    deleteSystemModule,
    getSystemModules,
    SystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdDelete,
    MdEdit,
    MdSearch,
    MdWidgets,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const getCardGradient = (index: number): string => {
    const gradients = [
        'from-secondary to-secondary/70',
        'from-secondary to-secondary/70',
        'from-accent to-accent/70',
        'from-info to-info/70',
        'from-success to-success/70',
        'from-warning to-warning/70',
        'from-error to-error/70',
    ]
    return gradients[index % gradients.length]
}

export default function SystemModulesPage() {
    const navigate = useNavigate()
    const [modules, setModules] = useState<SystemModule[]>([])
    const [filteredModules, setFilteredModules] = useState<SystemModule[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [deleteModalId, setDeleteModalId] = useState<number | null>(null)

    useEffect(() => {
        loadModules()
    }, [])

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredModules(modules)
            return
        }
        const term = searchTerm.toLowerCase()
        setFilteredModules(
            modules.filter(
                m =>
                    m.key.toLowerCase().includes(term) ||
                    m.label.toLowerCase().includes(term)
            )
        )
    }, [modules, searchTerm])

    const loadModules = async () => {
        setLoading(true)
        try {
            const response = await getSystemModules()
            if (response.success) {
                setModules(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar módulos')
            }
        } catch {
            toast.error('Erro ao carregar módulos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModalId) return
        try {
            const response = await deleteSystemModule(deleteModalId)
            if (response.success) {
                setModules(prev => prev.filter(m => m.id !== deleteModalId))
                toast.success('Módulo removido com sucesso')
            } else {
                toast.error(response.error || 'Erro ao remover módulo')
            }
        } catch {
            toast.error('Erro ao remover módulo')
        } finally {
            setDeleteModalId(null)
        }
    }

    if (loading && modules.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando módulos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdWidgets className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Módulos do Sistema
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {modules.length}{' '}
                            {modules.length === 1
                                ? 'módulo cadastrado'
                                : 'módulos cadastrados'}
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/system-modules/new')}
                    className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                >
                    <MdAdd className="text-lg" />
                    Novo Módulo
                </button>
            </div>

            {/* Search */}
            {modules.length > 0 && (
                <div className="relative max-w-md">
                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg z-10" />
                    <input
                        type="text"
                        placeholder="Buscar por chave ou nome..."
                        className="input input-bordered w-full pl-10"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            )}

            {filteredModules.length === 0 && modules.length > 0 ? (
                <div className="text-center py-12 text-base-content/60">
                    Nenhum módulo encontrado para "{searchTerm}"
                </div>
            ) : filteredModules.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdWidgets className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum módulo cadastrado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        Os módulos do sistema definem as áreas funcionais e seus
                        recursos de permissão.
                    </p>
                    <button
                        onClick={() => navigate('/system-modules/new')}
                        className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                    >
                        <MdAdd className="text-xl" />
                        Criar Primeiro Módulo
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                    {filteredModules.map((mod, index) => (
                        <div
                            key={mod.id}
                            onClick={() =>
                                navigate(`/system-modules/edit/${mod.id}`)
                            }
                            className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-2xl border border-base-300/50 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                            <div className="relative p-6">
                                {/* Header */}
                                <div className="flex items-start gap-4 mb-4">
                                    <div
                                        className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getCardGradient(index)} flex items-center justify-center shadow-lg flex-shrink-0`}
                                    >
                                        <MdWidgets className="text-2xl text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-base-content group-hover:text-secondary transition-colors line-clamp-1">
                                            {mod.label}
                                        </h3>
                                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                            <span className="badge badge-sm badge-ghost font-mono">
                                                {mod.key}
                                            </span>
                                            <span className="badge badge-sm badge-outline">
                                                Ordem: {mod.sortOrder}
                                            </span>
                                            {mod.tiers && mod.tiers.length > 0 ? (
                                                mod.tiers.map(t => (
                                                    <span
                                                        key={t.id}
                                                        className="badge badge-sm badge-secondary"
                                                    >
                                                        {t.name}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="badge badge-sm badge-warning badge-outline">
                                                    Sem tier
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                            <MdArrowForward className="text-base-content/40 group-hover:text-secondary transition-colors" />
                                        </div>
                                    </div>
                                </div>

                                {/* Recursos */}
                                <div className="mb-4">
                                    <p className="text-xs font-semibold text-base-content/50 uppercase tracking-wider mb-2">
                                        Recursos ({mod.resources.length})
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {mod.resources.slice(0, 6).map(res => (
                                            <span
                                                key={res.id}
                                                className="badge badge-sm badge-secondary badge-outline"
                                            >
                                                {res.label}
                                            </span>
                                        ))}
                                        {mod.resources.length > 6 && (
                                            <span className="badge badge-sm badge-ghost">
                                                +{mod.resources.length - 6}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="pt-4 border-t border-base-300/50">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/system-modules/edit/${mod.id}`
                                                    )
                                                }}
                                                title="Editar"
                                            >
                                                <MdEdit className="text-base" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-error"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    setDeleteModalId(mod.id)
                                                }}
                                                title="Remover"
                                            >
                                                <MdDelete className="text-base" />
                                            </button>
                                        </div>
                                        <div className="text-xs text-base-content/50">
                                            {format(
                                                new Date(mod.createdAt),
                                                'dd/MM/yyyy',
                                                { locale: ptBR }
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de confirmação de exclusão */}
            {deleteModalId && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Confirmar Exclusão
                        </h3>
                        <p className="py-4 text-base-content/70">
                            Tem certeza que deseja remover este módulo? Os
                            planos e roles que referenciam este módulo podem ser
                            afetados.
                        </p>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setDeleteModalId(null)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleDelete}
                            >
                                Remover
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setDeleteModalId(null)}
                    />
                </div>
            )}
        </div>
    )
}
